// Runtime aerodrome model: taxi graph, runways with their ends, holding points with line-up paths and
// take-off run available (TORA), runway exits, stands and constrained route search ("taxi via A, N").
import type { AirportGeo, AirportMeta, EdgeKind, RunwayEndMeta, TwyLimit } from '../data/types';
import { EDGE_KINDS } from '../data/types';
import { Projection, bearing, angDiff, dist, alongTrack, crossTrack, norm360, type XY } from './geo';

export interface GNode { id: number; x: number; y: number; runways: Runway[]; hold?: HoldPoint; stand?: Stand }
export interface GEdge { id: number; a: number; b: number; kind: EdgeKind; name: string; len: number; runway?: Runway }
export interface Link { to: number; e: GEdge }

export interface RunwayEnd {
  id: string;
  oppId: string;
  runway: Runway;
  /** Physical end of the runway where the take-off run starts. */
  start: XY;
  /** Landing threshold (displaced thresholds included). */
  thr: XY;
  /** True heading of the runway in this direction. */
  hdg: number;
  elevFt: number;
  meta: RunwayEndMeta;
  /** Departure holding points, longest TORA first. */
  depHolds: HoldPoint[];
  /** Exits for landings in this direction, ordered by distance from the threshold. */
  exits: RunwayExit[];
  /** Node at the very end of the runway (start of take-off run). */
  endNode: number;
}
export interface Runway {
  id: string;
  width: number;
  length: number;
  nodes: number[];
  ends: Record<string, RunwayEnd>;
  endIds: [string, string];
  closed: boolean;
}
export interface RunwayExit { hold: HoldPoint; rwyNode: number; along: number; path: number[]; angle: number; name: string }
export interface HoldPoint {
  node: number;
  name: string;
  type: 'runway' | 'ILS' | 'intermediate';
  runway: Runway | null;
  /** Lateral side of the runway (+1 = left of the first end's heading). */
  side: number;
  lineup: Record<string, number[]>;
  rwyNode: Record<string, number>;
  tora: Record<string, number>;
  exit: Record<string, RunwayExit>;
  /** Node beyond the holding point, away from the runway – where vacating aircraft are clear. */
  beyond: number | null;
  /** The CAT II/III holding point in front of this one, if any. */
  catHold?: number;
}
export interface Stand {
  id: string;
  node: number;
  lead: number[];
  hdg: number;
  say: string;
  maxSpan: number;
  push: boolean;
  cargo: boolean;
  ga: boolean;
  apronExit: number;
}

export interface PathOpts {
  /** Taxiway names to follow in order. */
  via?: string[];
  /** Stands whose lead-in lines may be used. */
  stands?: string[];
  span?: number;
  /** Runways the route may run along (backtrack). Crossing is always possible but expensive. */
  alongRunways?: Set<Runway>;
  avoidRunwayCrossing?: boolean;
  closedEdges?: Set<GEdge>;
  /** Edge names allowed in addition to the via list (e.g. the target holding point's taxiway). */
  extraNames?: string[];
  /** Operator (airline ICAO) – some taxiways are restricted to one operator (e.g. Airbus factory traffic). */
  operator?: string;
}

const CROSS_PENALTY = 700;
const kindIdx = (i: number): EdgeKind => EDGE_KINDS[i] ?? 'twy';

export class Airport {
  readonly icao: string;
  readonly proj: Projection;
  readonly nodes: GNode[];
  readonly edges: GEdge[];
  readonly adj: Link[][];
  readonly runways: Runway[] = [];
  readonly runwayEnds: Record<string, RunwayEnd> = {};
  readonly holds: HoldPoint[] = [];
  readonly stands: Record<string, Stand> = {};
  readonly towerXY: XY;
  readonly warnings: string[] = [];
  private readonly twyLimits: Record<string, TwyLimit>;

  constructor(readonly meta: AirportMeta, readonly geo: AirportGeo) {
    this.icao = meta.icao;
    this.proj = new Projection(geo.origin);
    this.twyLimits = meta.twyLimits ?? {};
    this.nodes = geo.nodes.map(([x, y], id) => ({ id, x, y, runways: [] }));
    this.edges = [];
    this.adj = this.nodes.map(() => []);
    for (const [a, b, k, n] of geo.edges) {
      const A = this.nodes[a]!, B = this.nodes[b]!;
      const e: GEdge = { id: this.edges.length, a, b, kind: kindIdx(k), name: geo.names[n] ?? '', len: dist(A.x, A.y, B.x, B.y) };
      this.edges.push(e);
      this.adj[a]!.push({ to: b, e });
      this.adj[b]!.push({ to: a, e });
    }
    const t = this.proj.toXY(meta.tower.lat, meta.tower.lon);
    this.towerXY = t;
    this.buildRunways();
    this.buildHolds();
    this.buildStands();
  }

  // ------------------------------------------------------------------ runways
  private buildRunways(): void {
    for (const gr of this.geo.runways) {
      const rm = this.meta.runways.find(r => r.osmRef === gr.osmRef);
      if (!rm) { this.warnings.push(`runway ${gr.osmRef} has no meta data`); continue; }
      const first = this.nodes[gr.nodes[0]!]!, last = this.nodes[gr.nodes[gr.nodes.length - 1]!]!;
      const hdg = bearing(first.x, first.y, last.x, last.y);
      const ids = rm.id.split('/') as [string, string];
      // assign the designator whose magnetic direction matches the direction first → last
      const magFirst = norm360(hdg - this.meta.magVar);
      const desig = (id: string) => Number.parseInt(id, 10) * 10;
      const firstId = Math.abs(angDiff(magFirst, desig(ids[0]))) < Math.abs(angDiff(magFirst, desig(ids[1]))) ? ids[0] : ids[1];
      const lastId = firstId === ids[0] ? ids[1] : ids[0];
      const length = dist(first.x, first.y, last.x, last.y);
      const rw: Runway = { id: rm.id, width: gr.width, length, nodes: gr.nodes, ends: {}, endIds: [firstId, lastId], closed: !!rm.closed };
      const mk = (id: string, opp: string, s: GNode, h: number, endNode: number): RunwayEnd => {
        const em = rm.ends[id] ?? {};
        let thr: XY = { x: s.x, y: s.y };
        if (em.thr) thr = this.proj.toXY(em.thr.lat, em.thr.lon);
        else if (em.displacedM) thr = { x: s.x + Math.sin(h * Math.PI / 180) * em.displacedM, y: s.y + Math.cos(h * Math.PI / 180) * em.displacedM };
        return { id, oppId: opp, runway: rw, start: { x: s.x, y: s.y }, thr, hdg: h, elevFt: em.elevFt ?? this.meta.elevFt, meta: em, depHolds: [], exits: [], endNode };
      };
      rw.ends[firstId] = mk(firstId, lastId, first, hdg, gr.nodes[0]!);
      rw.ends[lastId] = mk(lastId, firstId, last, norm360(hdg + 180), gr.nodes[gr.nodes.length - 1]!);
      for (let i = 0; i < gr.nodes.length - 1; i++) {
        const l = this.adj[gr.nodes[i]!]!.find(x => x.to === gr.nodes[i + 1]);
        if (l) l.e.runway = rw;
      }
      for (const n of gr.nodes) this.nodes[n]!.runways.push(rw);
      this.runways.push(rw);
      this.runwayEnds[firstId] = rw.ends[firstId]!;
      this.runwayEnds[lastId] = rw.ends[lastId]!;
    }
  }

  /** Distance from the runway end along its direction (negative = before the end). */
  along(end: RunwayEnd, p: XY): number { return alongTrack(p, end.start, end.hdg); }
  lateral(end: RunwayEnd, p: XY): number { return crossTrack(p, end.start, end.hdg); }
  /** Is the point within the runway strip used for occupancy (runway width + margin)? */
  onRunway(rw: Runway, p: XY, margin = 12): boolean {
    const e = rw.ends[rw.endIds[0]]!;
    const a = this.along(e, p);
    return Math.abs(this.lateral(e, p)) < rw.width / 2 + margin && a > -80 && a < rw.length + 80;
  }
  runwayAt(p: XY, margin = 12): Runway | null {
    for (const rw of this.runways) if (this.onRunway(rw, p, margin)) return rw;
    return null;
  }
  end(id: string): RunwayEnd {
    const e = this.runwayEnds[id];
    if (!e) throw new Error(`${this.icao}: unknown runway ${id}`);
    return e;
  }

  // ------------------------------------------------------------------ holding points
  private edgeName(node: number): string {
    const names = this.adj[node]!.filter(l => l.e.kind === 'twy' && l.e.name).map(l => l.e.name);
    if (names.length) return names.sort((a, b) => a.length - b.length)[0]!;
    return this.adj[node]!.find(l => l.e.name)?.e.name ?? '';
  }

  private linksToRunway(start: number, maxLen = 320): { path: number[]; len: number; rwy: Runway; rn: number }[] {
    const D = new Map<number, number>([[start, 0]]), prev = new Map<number, number>();
    const open: [number, number][] = [[0, start]];
    const found: { path: number[]; len: number; rwy: Runway; rn: number }[] = [];
    const done = new Set<number>();
    while (open.length) {
      open.sort((a, b) => a[0] - b[0]);
      const [du, u] = open.shift()!;
      if (done.has(u)) continue;
      done.add(u);
      const n = this.nodes[u]!;
      if (n.runways.length && u !== start) {
        const p = [u];
        while (p[0] !== start) p.unshift(prev.get(p[0]!)!);
        for (const rw of n.runways) found.push({ path: p, len: du, rwy: rw, rn: u });
        continue;
      }
      if (du > maxLen) continue;
      for (const { to, e } of this.adj[u]!) {
        if (e.kind === 'rwy' || e.kind === 'stand') continue;
        const nd = du + e.len;
        if (!D.has(to) || nd < D.get(to)!) { D.set(to, nd); prev.set(to, u); open.push([nd, to]); }
      }
    }
    return found;
  }

  private buildHolds(): void {
    for (const gh of this.geo.holds) {
      const name = gh.ref || this.edgeName(gh.node);
      const links = this.linksToRunway(gh.node);
      const nearest = links.reduce<typeof links[number] | null>((m, l) => (!m || l.len < m.len ? l : m), null);
      const hp: HoldPoint = { node: gh.node, name, type: gh.type, runway: null, side: 0, lineup: {}, rwyNode: {}, tora: {}, exit: {}, beyond: null };
      const n = this.nodes[gh.node]!;
      n.hold = hp;
      this.holds.push(hp);
      if (gh.type !== 'runway' || !nearest || nearest.len > 260) continue;
      const rw = nearest.rwy;
      if (rw.closed) continue;
      hp.runway = rw;
      const mine = links.filter(l => l.rwy === rw && l.len < nearest.len + 160);
      const e0 = rw.ends[rw.endIds[0]]!;
      hp.side = Math.sign(-this.lateral(e0, n)) || 1;
      const towardRwy = new Set(mine.map(l => l.path[1]));
      hp.beyond = this.adj[gh.node]!.map(l => l.to).find(id => !towardRwy.has(id) && !this.nodes[id]!.runways.length) ?? null;
      for (const endId of rw.endIds) {
        const end = rw.ends[endId]!;
        // line-up: link whose last segment is closest to the take-off direction
        let best: typeof mine[number] | null = null, bc = Infinity;
        for (const l of mine) {
          const p = l.path, a = this.nodes[p[p.length - 2]!]!, b = this.nodes[p[p.length - 1]!]!;
          const turn = Math.abs(angDiff(bearing(a.x, a.y, b.x, b.y), end.hdg));
          const c = l.len + turn * 2.5;
          if (c < bc) { bc = c; best = l; }
        }
        if (!best) continue;
        hp.lineup[endId] = best.path;
        hp.rwyNode[endId] = best.rn;
        const rn = this.nodes[best.rn]!;
        hp.tora[endId] = end.meta.tora?.[name] ?? Math.round(rw.length - this.along(end, rn));
        // exit after landing in this direction: from the runway node, turning forward
        let ex: { path: number[]; ang: number } | null = null; bc = Infinity;
        for (const l of mine) {
          const p = [...l.path].reverse(), a = this.nodes[p[0]!]!, b = this.nodes[p[1]!]!;
          const ang = Math.abs(angDiff(end.hdg, bearing(a.x, a.y, b.x, b.y)));
          const c = l.len + ang * 3;
          if (c < bc) { bc = c; ex = { path: p, ang }; }
        }
        if (ex) {
          const path = ex.path.slice();
          if (hp.beyond !== null) path.push(hp.beyond);
          hp.exit[endId] = { hold: hp, rwyNode: path[0]!, along: this.along(end, this.nodes[path[0]!]!), path, angle: ex.ang, name };
        }
      }
    }
    // CAT II/III holding points belonging to a runway holding point
    for (const hp of this.holds) {
      if (hp.type !== 'runway' || !hp.runway) continue;
      const cat = this.holds.filter(h => h.type === 'ILS' && h.name === hp.name)
        .map(h => ({ h, p: this.findPath(h.node, hp.node) })).filter(x => x.p && this.pathLength(x.p) < 260)
        .sort((a, b) => this.pathLength(a.p!) - this.pathLength(b.p!))[0];
      if (cat) hp.catHold = cat.h.node;
    }
    for (const rw of this.runways) {
      for (const endId of rw.endIds) {
        const end = rw.ends[endId]!;
        const toraTable = end.meta.tora;
        const cands = this.holds.filter(h => h.runway === rw && h.tora[endId] !== undefined);
        end.depHolds = cands
          .filter(h => (toraTable ? toraTable[h.name] !== undefined : h.tora[endId]! > 900))
          .filter((h, i, arr) => arr.findIndex(o => o.name === h.name) === i || !toraTable)
          .sort((a, b) => b.tora[endId]! - a.tora[endId]!);
        end.exits = cands.map(h => h.exit[endId]).filter((x): x is RunwayExit => !!x && x.along > 250 && x.along < rw.length - 60)
          .filter(x => !end.meta.exits || end.meta.exits.includes(x.name))
          .sort((a, b) => a.along - b.along);
      }
    }
  }

  // ------------------------------------------------------------------ stands
  private buildStands(): void {
    const sm = this.meta.stands;
    const count: Record<string, number> = {};
    for (const gs of this.geo.stands) {
      if (sm.drop?.includes(gs.ref)) continue;
      let id = sm.map?.[gs.ref] ?? gs.ref;
      if (!id) id = 'P';
      const numbered = gs.ref !== '';
      // the same stand number twice in OSM (alternative positions) – only the first one is used
      if (numbered && this.stands[id]) continue;
      count[id] = (count[id] ?? 0) + 1;
      if (this.stands[id]) id = `${id}-${count[id]}`;
      const info = sm.info?.[id] ?? sm.info?.[id.replace(/-\d+$/, '')] ?? {};
      const say = info.say ?? `stand ${spokenStand(id)}`;
      const apronExit = gs.lead[gs.lead.length - 1]!;
      const st: Stand = { id, node: gs.node, lead: gs.lead, hdg: gs.hdg, say, maxSpan: info.maxSpan ?? sm.defaultMaxSpan ?? 36, push: info.push ?? true, cargo: !!info.cargo, ga: !!info.ga, apronExit };
      this.stands[id] = st;
      this.nodes[gs.node]!.stand = st;
    }
  }

  // ------------------------------------------------------------------ routing
  edgeAllowed(e: GEdge, span: number, operator?: string): boolean {
    const l = this.twyLimits[e.name];
    if (!l) return true;
    if (l.restricted) return operator === l.restricted;
    if (l.blockedFromSpan !== undefined && span >= l.blockedFromSpan) return false;
    if (l.maxSpan !== undefined && span > l.maxSpan) return false;
    return true;
  }
  edgeBetween(a: number, b: number): GEdge | null { return this.adj[a]?.find(l => l.to === b)?.e ?? null; }

  /**
   * Shortest route between two nodes. With opts.via the route has to follow the named taxiways in order
   * (unnamed connectors and apron lanes before the first named taxiway are allowed).
   */
  findPath(from: number, to: number, opts: PathOpts = {}): number[] | null {
    if (from === to) return [from];
    const via = opts.via?.filter(Boolean) ?? [];
    const span = opts.span ?? 0;
    const extra = new Set(opts.extraNames ?? []);
    const standNodes = new Set<number>();
    for (const sid of opts.stands ?? []) { const s = this.stands[sid]; if (s) for (const n of s.lead) standNodes.add(n); }
    // state key = node * 64 + stage; stage = index of the via entry we are on (-1 → 63 "before first")
    const PRE = 63;
    const key = (n: number, s: number) => n * 64 + s;
    const D = new Map<number, number>(), prev = new Map<number, number>();
    const startStage = via.length ? PRE : 0;
    const start = key(from, startStage);
    D.set(start, 0);
    const heap = new MinHeap();
    heap.push(start, 0);
    const done = new Set<number>();
    let goal = -1;
    while (heap.size) {
      const [k, du] = heap.pop()!;
      if (done.has(k)) continue;
      done.add(k);
      const u = Math.floor(k / 64), st = k % 64;
      if (u === to && (!via.length || st === via.length - 1 || st === via.length)) { goal = k; break; }
      for (const { to: v, e } of this.adj[u]!) {
        if (opts.closedEdges?.has(e)) continue;
        if (e.kind === 'stand' && !(standNodes.has(u) && standNodes.has(v))) continue;
        if (e.kind === 'rwy' && !(e.runway && opts.alongRunways?.has(e.runway))) continue;
        if (!this.edgeAllowed(e, span, opts.operator)) continue;
        let ns = st;
        if (via.length) {
          const named = e.name !== '' && e.kind !== 'stand';
          if (st === PRE) {
            if (named && e.name === via[0]) ns = 0;
            else if (named && e.kind === 'twy' && !extra.has(e.name)) continue;
          } else {
            const cur = via[st], nxt = via[st + 1];
            if (!named || e.name === cur || e.kind === 'rwy') ns = st;
            else if (e.name === nxt) ns = st + 1;
            else if (st >= via.length - 1 && (extra.has(e.name) || e.kind === 'lane')) ns = via.length;
            else continue;
          }
        }
        let cost = e.len;
        const vn = this.nodes[v]!;
        if (vn.runways.length && e.kind !== 'rwy' && !this.nodes[u]!.runways.length) cost += opts.avoidRunwayCrossing ? 1e6 : CROSS_PENALTY;
        const nk = key(v, ns), nd = du + cost;
        if (!D.has(nk) || nd < D.get(nk)!) { D.set(nk, nd); prev.set(nk, k); heap.push(nk, nd); }
      }
    }
    if (goal < 0) return null;
    const path: number[] = [];
    for (let k: number | undefined = goal; k !== undefined; k = prev.get(k)) path.unshift(Math.floor(k / 64));
    return path.filter((n, i) => i === 0 || n !== path[i - 1]);
  }

  pathLength(path: number[]): number {
    let s = 0;
    for (let i = 0; i < path.length - 1; i++) { const a = this.nodes[path[i]!]!, b = this.nodes[path[i + 1]!]!; s += dist(a.x, a.y, b.x, b.y); }
    return s;
  }
  /** Taxiway names along a path, without repeats – the "via" part of a taxi clearance. */
  routeNames(path: number[]): string[] {
    const out: string[] = [];
    for (let i = 0; i < path.length - 1; i++) {
      const e = this.edgeBetween(path[i]!, path[i + 1]!);
      if (e && e.kind !== 'stand' && e.kind !== 'rwy' && e.name && out[out.length - 1] !== e.name) out.push(e.name);
    }
    return out;
  }
  /** Runways crossed (entered from a taxiway and left again) along a path, with the node index of entry. */
  runwayCrossings(path: number[]): { runway: Runway; index: number }[] {
    const out: { runway: Runway; index: number }[] = [];
    for (let i = 1; i < path.length; i++) {
      const n = this.nodes[path[i]!]!, p = this.nodes[path[i - 1]!]!;
      for (const rw of n.runways) if (!p.runways.includes(rw) && !out.some(o => o.runway === rw && i - o.index < 6)) out.push({ runway: rw, index: i });
    }
    return out;
  }
  nearestNode(p: XY, filter?: (n: GNode) => boolean): number {
    let best = 0, bd = Infinity;
    for (const n of this.nodes) { if (filter && !filter(n)) continue; const d = dist(p.x, p.y, n.x, n.y); if (d < bd) { bd = d; best = n.id; } }
    return best;
  }
  /** Departure holding point by taxiway name for a runway end. */
  depHold(endId: string, name?: string): HoldPoint | null {
    const end = this.runwayEnds[endId];
    if (!end) return null;
    if (!name) return end.depHolds[0] ?? null;
    return end.depHolds.find(h => h.name === name) ?? this.holds.find(h => h.name === name && h.runway === end.runway && h.lineup[endId]) ?? null;
  }
  holdByName(name: string, rw?: Runway): HoldPoint | null {
    return this.holds.find(h => h.name === name && h.type === 'runway' && (!rw || h.runway === rw)) ?? null;
  }
  /** Taxiway names known at this airport (for parsing and autocompletion). */
  taxiwayNames(): string[] {
    return [...new Set(this.edges.filter(e => e.kind !== 'stand' && e.kind !== 'rwy' && e.name).map(e => e.name))].sort();
  }
}

export function spokenStand(id: string): string {
  return id.replace(/^0+(?=\d)/, '');
}

class MinHeap {
  private a: [number, number][] = [];
  get size(): number { return this.a.length; }
  push(v: number, k: number): void {
    const a = this.a;
    a.push([v, k]);
    let i = a.length - 1;
    while (i > 0) { const p = (i - 1) >> 1; if (a[p]![1] <= a[i]![1]) break; [a[p], a[i]] = [a[i]!, a[p]!]; i = p; }
  }
  pop(): [number, number] | undefined {
    const a = this.a;
    const top = a[0];
    const last = a.pop();
    if (a.length && last) {
      a[0] = last;
      let i = 0;
      for (;;) {
        const l = 2 * i + 1, r = l + 1;
        let m = i;
        if (l < a.length && a[l]![1] < a[m]![1]) m = l;
        if (r < a.length && a[r]![1] < a[m]![1]) m = r;
        if (m === i) break;
        [a[m], a[i]] = [a[i]!, a[m]!];
        i = m;
      }
    }
    return top;
  }
}
